import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../Components/AuthProvider";
import { checkInTicket, lookupTicket } from "./ticketOpsApi";
import TicketQr from "./TicketQr";
import TicketStatusBadge from "./TicketStatusBadge";
import { getHostedTicketEventDetails } from "./hostedTicketEvents";
import "./rockin-road-home.css";

const CHECKIN_ROLES = new Set(["admin", "outreach"]);

function attendeeName(ticket) {
  return [ticket?.attendee_first_name, ticket?.attendee_last_name].filter(Boolean).join(" ");
}

function formatEventDate(ticket) {
  if (!ticket?.event_date_utc) return "";
  const timezone = ticket.event_timezone || "America/Chicago";
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeZone: timezone,
  }).format(new Date(ticket.event_date_utc));
}

function currentTicketUrl() {
  if (typeof window === "undefined") return "";
  return window.location.href;
}

function StatusMessage({ data, error }) {
  if (error) {
    return <p className="rrh-state-message rrh-state-message--error">{error}</p>;
  }
  if (!data) return null;
  if (data.found === false) {
    return <p className="rrh-state-message rrh-state-message--error">Ticket not found or this link has expired.</p>;
  }
  if (data.already_checked_in) {
    return <p className="rrh-state-message rrh-state-message--success">This ticket has already been checked in.</p>;
  }
  const status = data.ticket?.ticket_status;
  if (status === "cancelled" || status === "void") {
    return <p className="rrh-state-message rrh-state-message--warning">This ticket is not active and cannot be checked in.</p>;
  }
  if (data.checked_in) {
    return <p className="rrh-state-message rrh-state-message--success">Check-in complete.</p>;
  }
  return <p className="rrh-state-message">Show this QR code to event staff for check-in.</p>;
}

export default function TicketCheckinPage() {
  const { token } = useParams();
  const { profile, user, loading: authLoading } = useContext(AuthContext);
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [actionStatus, setActionStatus] = useState("idle");
  const ticketUrl = useMemo(() => currentTicketUrl(), []);

  const role = String(profile?.role || "").toLowerCase();
  const isAllowedStaff = Boolean(user && CHECKIN_ROLES.has(role));
  const ticket = data?.ticket;
  const eventDetails = getHostedTicketEventDetails(ticket?.event_id);
  const canCheckIn =
    isAllowedStaff &&
    data?.found !== false &&
    ticket?.ticket_status === "active" &&
    actionStatus !== "submitting";

  useEffect(() => {
    let cancelled = false;

    async function loadTicket() {
      setStatus("loading");
      setMessage("");
      const response = await lookupTicket(token);
      if (cancelled) return;

      setData(response);
      setStatus("loaded");
      if (response?.ok === false && response?.error) {
        setMessage(response.error);
      }
    }

    loadTicket();

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleCheckIn() {
    if (!canCheckIn) return;

    setActionStatus("submitting");
    setMessage("");
    const response = await checkInTicket(token, "qr_scan");
    setData(response);
    setActionStatus("idle");

    if (response?.code === "UNAUTHENTICATED") {
      setMessage("A staff sign-in is required before checking in tickets.");
      return;
    }
    if (response?.code === "ROLE_NOT_ALLOWED") {
      setMessage("Your staff profile is not allowed to check in tickets.");
      return;
    }
    if (response?.ok === false && response?.error) {
      setMessage(response.error);
    }
  }

  return (
    <main className="rrh-page rrh-ops-page">
      <section className="rrh-ops-panel" aria-labelledby="rrh-ticket-checkin-heading">
        <div>
          <p className="rrh-section__eyebrow">Event ticket</p>
          <h1 id="rrh-ticket-checkin-heading">
            {eventDetails?.ticketHeading || "Road Home LIVE Ticket"}
          </h1>
        </div>

        {status === "loading" ? (
          <p className="rrh-state-message">Loading ticket...</p>
        ) : (
          <>
            <StatusMessage data={data} error={message} />

            {ticket && (
              <div className="rrh-ops-grid">
                <div className="rrh-ticket-summary">
                  <dl>
                    <div>
                      <dt>Event</dt>
                      <dd>{ticket.event_name || "Road Home LIVE"}</dd>
                    </div>
                    {formatEventDate(ticket) && (
                      <div>
                        <dt>Date</dt>
                        <dd>{formatEventDate(ticket)}</dd>
                      </div>
                    )}
                    {eventDetails?.dateTime && (
                      <div>
                        <dt>Time</dt>
                        <dd>{eventDetails.dateTime}</dd>
                      </div>
                    )}
                    {eventDetails?.venue && (
                      <div>
                        <dt>Venue</dt>
                        <dd>{eventDetails.venue}<br />{eventDetails.address}</dd>
                      </div>
                    )}
                    <div>
                      <dt>Attendee</dt>
                      <dd>{attendeeName(ticket) || "Guest"}</dd>
                    </div>
                    <div>
                      <dt>Ticket code</dt>
                      <dd>{ticket.ticket_code || "Unavailable"}</dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd><TicketStatusBadge status={ticket.ticket_status} /></dd>
                    </div>
                  </dl>
                </div>

                <TicketQr value={ticketUrl} label="Scan for this ticket" />
              </div>
            )}

            {ticket && !authLoading && !user && (
              <p className="rrh-state-message rrh-state-message--staff">Staff sign-in is required to check in this ticket.</p>
            )}

            {ticket && user && !authLoading && !isAllowedStaff && (
              <p className="rrh-state-message rrh-state-message--warning">Your signed-in profile does not have ticket check-in permission.</p>
            )}

            {ticket && isAllowedStaff && (
              <section className="rrh-ops-form" aria-labelledby="rrh-staff-checkin-heading">
                <h2 id="rrh-staff-checkin-heading">Staff-only check-in</h2>
                <button
                  className="rrh-button rrh-button--primary rrh-ops-action"
                  disabled={!canCheckIn}
                  onClick={handleCheckIn}
                  type="button"
                >
                  {actionStatus === "submitting" ? "Checking in..." : "Check In Ticket"}
                </button>
              </section>
            )}
          </>
        )}
      </section>
    </main>
  );
}
